import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';

/* ── Types ── */

export interface DocumentVersion {
  id: string;
  documentId: string;
  version: number;
  fileName: string;
  fileSize: number;
  mimeType: string;
  uploadedBy: string;
  uploadedByName?: string;
  notes?: string;
  createdAt: string;
  downloadUrl: string;
}

export interface DocumentLink {
  id: string;
  entityType: string;
  entityId: string;
  entityName?: string;
}

/**
 * Espelha `DocumentResponseDto`.
 *
 * O arquivo (nome, tipo, tamanho) pertence a VERSAO, nao ao documento: um
 * documento versionado troca de arquivo a cada versao. A tela lia esses campos
 * no nivel do documento e mostrava cartao sem titulo e "0 B".
 */
export interface Document {
  id: string;
  tenantId: string;
  companyId: string;
  category: string;
  title: string;
  description?: string | null;
  tags: string[];
  status: string;
  retentionDays?: number | null;
  expiresAt?: string | null;
  currentVersionId?: string | null;
  currentVersionNumber?: number | null;
  currentVersion?: DocumentVersion | null;
  versions?: DocumentVersion[];
  links?: DocumentLink[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentInput {
  name: string;
  category: string;
  tags?: string[];
  companyId?: string;
}

export interface DocumentUpdateInput {
  name?: string;
  category?: string;
  tags?: string[];
  companyId?: string;
}

interface PaginatedDocuments {
  data: Document[];
  total: number;
  page: number;
  limit: number;
}

/* ── Helpers ── */

function buildDocQueryKey(params?: {
  search?: string;
  category?: string;
  tags?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}) {
  return ['documents', 'list', params ?? {}] as const;
}

/* ── Hooks ── */

export function useDocuments(params?: {
  search?: string;
  category?: string;
  tags?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}): ReturnType<typeof useQuery<PaginatedDocuments>> {
  return useQuery<PaginatedDocuments>({
    queryKey: buildDocQueryKey(params),
    queryFn: async () =>
      apiClient.get<PaginatedDocuments>('/documents', {
        params: {
          ...(params?.search ? { search: params.search } : {}),
          ...(params?.category ? { category: params.category } : {}),
          ...(params?.tags ? { tags: params.tags } : {}),
          ...(params?.dateFrom ? { dateFrom: params.dateFrom } : {}),
          ...(params?.dateTo ? { dateTo: params.dateTo } : {}),
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 20),
        },
      }),
  });
}

export function useDocument(id: string): ReturnType<typeof useQuery<Document>> {
  return useQuery<Document>({
    queryKey: ['documents', id] as const,
    queryFn: async () => apiClient.get<Document>(`/documents/${id}`),
    enabled: !!id,
  });
}

export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation<Document, unknown, FormData, unknown>({
    mutationFn: async (formData: FormData) => {
      const response = await fetch(`/api/v1/documents`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const backendError = errorData as {
          error?: { status: number; code: string; message: string };
        };
        const message = backendError.error?.message ?? 'Erro ao upload documento';
        throw new Error(message);
      }

      return response.json() as Promise<Document>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

export function useUpdateDocument() {
  const queryClient = useQueryClient();

  return useMutation<Document, unknown, { id: string; input: DocumentUpdateInput }, unknown>({
    mutationFn: ({ id, input }) =>
      apiClient.put<Document>(`/documents/${id}`, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.setQueryData(['documents', data.id], data);
    },
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, string, unknown>({
      mutationFn: (id: string) => apiClient.delete(`/documents/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

export function useUploadVersion() {
  const queryClient = useQueryClient();

  return useMutation<Document, unknown, { id: string; formData: FormData }, unknown>({
    mutationFn: async ({ id, formData }) => {
      const response = await fetch(`/api/v1/documents/${id}/versions`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const backendError = errorData as {
          error?: { status: number; code: string; message: string };
        };
        const message = backendError.error?.message ?? 'Erro ao upload versao';
        throw new Error(message);
      }

      return response.json() as Promise<Document>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}